import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { NbToastrService } from '@nebular/theme';
import { environment } from '../../../environments/environment';

export interface ApiResponse<T> {
  status: string;
  message?: string;
  data?: T;
}

export interface UploadResult {
  percent: number;
  message?: string;
  weburl?: string;
}

@Injectable({
  providedIn: 'root'
})
export class UploadService {
  baseUrl = environment.api.core.base;
  path = environment.api.genie.upload.endpoint;
  private backendUrl = this.baseUrl + this.path;
  // NOTE: Changed to 500 MB based on your initial request.
  private readonly MAX_FILE_SIZE = 500 * 1024 * 1024; // 500 MB

  constructor(private http: HttpClient, private toastrService: NbToastrService) {}

  async uploadFileInChunks(
    file: File,
    accessToken: string,
    bearerToken: string,
    sessionId: string,
    onProgress?: (percent: number) => void
  ): Promise<ApiResponse<UploadResult>> { // ✅ CHANGE: Return the final API response
    if (file.size > this.MAX_FILE_SIZE) {
      this.toastrService.danger('File size exceeds the maximum limit of 500 MB.', 'Error', { duration: 5000 });
      throw new Error(`File size exceeds the maximum limit of 500 MB.`);
    }

    const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB 
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

    let finalResponse: ApiResponse<UploadResult> | undefined;

    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      const metadata = {
        chunkIndex: i,
        totalChunks,
        originalFileName: file.name,
        totalFileSize: file.size
      };

      const formData = new FormData();
      formData.append('fileToken',accessToken)
      formData.append('file', chunk, file.name);
      formData.append(
        'metadata',
        new Blob([JSON.stringify(metadata)], { type: 'application/json' })
      );

      const headers = new HttpHeaders({
        Authorization: `Bearer ${bearerToken}`,
        //'file-upload-Token': accessToken
      });

      const url = `${this.backendUrl}?sessionId=${encodeURIComponent(sessionId)}`;
      
      const response = await firstValueFrom(
        this.http.post<ApiResponse<UploadResult>>(url, formData, { headers })
      );
      
      // Update progress from the response
      if (onProgress) {
        const percent = response?.data?.percent ?? 0;
        onProgress(percent);
      }

      // Store the response from the final chunk
      if (i === totalChunks - 1) {
        finalResponse = response;
      }
    }

    if (!finalResponse) {
      // Handle case where no chunks were uploaded (e.g., empty file)
      throw new Error('Upload process failed to complete.');
    }
    
    // Return the final response
    return finalResponse;
  }
}